import {ScrollHelper} from '../../../shared/services/scroll-helper';
import {DialogAction, DialogKey} from '../../../shared/components/dialogs/dialogs-configuration';
import {DialogService} from '../dialog.service';
import {ApplicationStateService} from '../../../shared/services/application-state.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {ConfigurationService} from './configuration/configuration.service';
import {Subscription} from 'rxjs/Rx';
import {OfferService} from './offer.service';
import {EventEmitter} from '@angular/core';
import {AppMode} from '../../../shared/models/app-mode.enum';
import {InsuranceCompanyCode} from '../../../shared/models/insurance-company-code.enum';
import {InsuranceCompanyService} from '../../../shared/services/insurance-company.service';
import {Offer} from '../../models/offer/offer.model';
import {InsuranceCompanyLogoRole} from '../../models/insurance-company-config';
import {ImageDefinition} from '../../../shared/models/image-model';
import {BusinessParamName} from '../../../shared/models/request-offer/request-offer.enum';
import {TestingPresetsService} from '../../../shared/services/debugging/testing-presets.service';
import {TestingPresetKey} from '../../enums/debugging/testing-preset-key.enum';
import {DeviceService} from '../../../shared/services/device.service';

export class OfferDialogService {

  insuranceCompaniesOffersState: InsuranceCompanyOffersState[];
  dialogClosed: EventEmitter<void> = new EventEmitter<void>();
  cancelMatchingOfferEvent: EventEmitter<any> = new EventEmitter();
  dialogClosingEvent: EventEmitter<any> = new EventEmitter();
  animationAccelerationEnabled: boolean = false;
  private dialogDisplayTimeout: number;
  private websocketCloseTimeout: number;
  private dialogKey;

  constructor(private dialogService: DialogService,
              private configuration: ConfigurationService,
              private stateService: ApplicationStateService,
              private offerService: OfferService,
              private insuranceCompanyService: InsuranceCompanyService,
              private deviceService: DeviceService) {

  }

  get isWaitingForOfferMode(): boolean {
    return this.stateService.mode === AppMode.WAITING_FOR_OFFER || this.stateService.mode === AppMode.WAITING_FOR_OFFER_LOADING;
  }

  get isDialogOpened(): boolean {
    return this.dialogService.isOpened(this.dialogKey);
  }

  get offersNotFound(): boolean {
    return this.offerService.offers.length === 0;
  }

  get dialogDisplayTime(): number {
    const dialogConfig = this.dialogKey === DialogKey.LOADING_OFFERS ? this.configuration.uiConfig.loadingOfferDialogConfig : this.configuration.uiConfig.waitingOfferDialogConfig;
    if (dialogConfig) {
      return dialogConfig.displayTime * 1000;
    }
  }

  get timeBetweenDialogAndWebsocketClose(): number {
    return this.offerService.wsOpenedTime - this.dialogDisplayTime;
  }

  get dialogImageUrl(): string {
    if (this.stateService.mode === AppMode.WAITING_FOR_OFFER) {
      return this.configuration.uiConfig.loadingOfferDialogConfig.imageSrc;
    } else {
      return this.configuration.uiConfig.waitingOfferDialogConfig.imageSrc;
    }
  }

  initWaitingOfferDialog(): void {
    setTimeout(() => {
      if (!this.offerService.offerMatched) {
        this.open();
        this.handleAllOffersReadyEvent();
        this.dialogDisplayTimeout = window.setTimeout(() => { // dialog display time has elapsed (2,3,4,5)
          if (this.isDialogOpened) {
            if (!this.offersNotFound) { // some offers were found (2,5)
              this.dialogClosingEvent.emit();
              setTimeout(() => this.close(), 1000);
            } else { // no offers were found (3,4)
              this.handleNoOffersFoundAfterDialogDisplayTimeout();
            }
          }
        }, this.dialogDisplayTime); // close when max display time has elapsed
      }
    }, ScrollHelper.offersDialogOpenTimeout);
  }

  open(): void {
    if (!TestingPresetsService.isPresetActive(TestingPresetKey.REJECT_ALL_OFFERS)) {
      this.dialogKey = this.stateService.mode === AppMode.WAITING_FOR_OFFER ? DialogKey.LOADING_OFFERS : DialogKey.OFFERS_PROGRESS;
      const scrollHelper = new ScrollHelper();
      if (this.isWaitingForOfferMode) {
        scrollHelper.scrollToTop();
        this.stateService.mode = AppMode.WAITING_FOR_OFFER_LOADING;
      }
      const dialogOpenTimeout = this.deviceService.isSafari ? 100 : 0;
      setTimeout(() => {
        this.dialogService.open(this.dialogKey, {
          offerDialogService: this
        });
        scrollHelper.disableScrolling();
        this.dialogService.addAction(this.dialogKey, {
          actionKey: DialogAction.ENABLE_SCROLLING,
          callback: () => {
            scrollHelper.enableScrolling();
            this.cancelMatchingOfferEvent.emit();
            this.dialogClosed.emit();
          },
          isDefault: true
        });
      }, dialogOpenTimeout);
    }
  }

  close(): void {
    if (this.isDialogOpened) {
      this.dialogService.close(this.dialogKey, DialogAction.ENABLE_SCROLLING);
    }
    this.dialogClosed.emit();
  }

  resetState(): void {
    if (isNotNullOrUndefined(this.dialogDisplayTimeout)) {
      clearTimeout(this.dialogDisplayTimeout);
    }
    if (isNotNullOrUndefined(this.websocketCloseTimeout)) {
      clearTimeout(this.websocketCloseTimeout);
    }
  }

  openMissingPersonalDataDialog(): void {
    this.dialogService.open(DialogKey.MISSING_PERSONAL_DATA);
  }

  initInsuranceCompaniesOffersState(): void {
    this.insuranceCompaniesOffersState = this.insuranceCompanyService.getInsuranceCompaniesForOffersProgressDialog()
        .map(config => {
          const icLogo = config.logos ? config.logos.find(logo => logo.role === InsuranceCompanyLogoRole.OFFERS_PROGRESS_DIALOG) : null
          return {
            code: config.code,
            logo: icLogo ? icLogo.image : null,
            correctOffersAmount: 0
          }
        });
  }

  updateInsuranceCompanyOffersState(offer: Offer): void {
    const icCode = this.stateService.getBusinessParamFromOffer(offer.id, BusinessParamName.INSURANCE_COMPANY_CODE);
    if (isNotNullOrUndefined(this.getInsuranceCompanyOffersState(icCode))) {
      this.getInsuranceCompanyOffersState(icCode).correctOffersAmount++;
    }
  }

  private handleAllOffersReadyEvent(): void {
    this.offerService.allOffersReadyEvent.take(1).subscribe(() => { // close when all offers are ready (1)
      setTimeout(() => {
        if (this.offersNotFound) {
          this.close();
          this.offerService.noOffersEvent.emit(); // display no offers found dialog
        }
      }, 2000);
    });
  }

  private handleNoOffersFoundAfterDialogDisplayTimeout(): void {
    const offersCounter$ = this.handleOfferAfterDialogDisplayTime(); // close dialog when offer appears (4)
    this.websocketCloseTimeout = window.setTimeout(() => {
      if (this.offersNotFound) { // WS is closed & no offers were found (3)
        this.dialogClosingEvent.emit();
        setTimeout(() => {
          this.close();
          this.offerService.noOffersEvent.emit(); // display no offers found dialog
        }, 2000);
      }
      offersCounter$.unsubscribe();
    }, this.timeBetweenDialogAndWebsocketClose);
  }

  private getInsuranceCompanyOffersState(code: string): InsuranceCompanyOffersState {
    return this.insuranceCompaniesOffersState.find(item => item.code === code);
  }

  private handleOfferAfterDialogDisplayTime(): Subscription {
    return this.offerService.$offersChange.take(1).subscribe(() => {
      setTimeout(() => this.close(), 1000);
    });
  }

}

export interface InsuranceCompanyOffersState {
  code: InsuranceCompanyCode;
  correctOffersAmount: number;
  logo?: ImageDefinition;
}
